import {
  ArrowRight,
  Check,
  CircleDot,
  Compass,
  Lightbulb,
  Rocket,
  Sparkles,
  Users,
} from "lucide-react"
import { Link } from "react-router-dom"
import { type CalendlyCtaLocation, calendlyEventOwnerName } from "~/common/calendly"
import { cn } from "~/common/cn"
import {
  aiStrategyPath,
  acceleratorsPath,
  designPath,
  earlyStartupsPath,
  growthStartupsPath,
  privateEquityPath,
  productPath,
  smallMediumBusinessesPath,
  sprintZeroPath,
  webDevelopmentPath,
  workDetailPath,
  workPath,
} from "~/common/paths"
import { assetManifest } from "~/content/assets"
import { caseStudies, type CaseStudyId } from "~/content/cases"
import { type DudaCopyPageId } from "~/content/duda-copy"
import { marketingPages } from "~/content/marketing-pages/duda-pages"
import { ResponsiveImage } from "~/content/responsive-image"
import { services } from "~/content/services"
import type { ServiceDetailPage } from "~/content/services/detail-pages"
import { solutions } from "~/content/solutions"
import { teamMemberIds, teamMembers } from "~/content/team"
import type { Service, Solution } from "~/content/types"
import { LandingPage } from "~/landing/landing-page"
import { ContactCta } from "~/landing/marketing-sections"
import { trackCtaClick } from "~/lib/analytics"
import { BrandBand } from "~/marketing/brand-band"
import { CaseStudyCard } from "~/marketing/case-study-card"
import { FirstTimeFoundersPage } from "~/marketing/first-time-founders-page"
import { HeadlineText } from "~/marketing/headline-text"
import { ContactForm, NewsletterForm } from "~/marketing/lead-forms"
import { ScheduleLink } from "~/marketing/schedule-link"
import { ServicePage } from "~/marketing/service-page"
import { SolutionDetailPage, SolutionsOverviewPage } from "~/marketing/solution-page"
import { Card, CardContent, CardHeader, CardTitle } from "~/ui/card"

const staticPages = {
  "our-approach": {
    eyebrow: "How we work",
    heading: "The right team, focused on the right work.",
    body: "We combine product strategy, design, and engineering in one accountable partnership. We start by aligning on the problem, make practical decisions together, and keep momentum through launch and beyond.",
  },
  "privacy": {
    eyebrow: "Privacy policy",
    heading: "Your information should be handled with care.",
    body: "We collect only the information needed to respond to inquiries and provide our services. We do not sell personal information. We use reasonable safeguards to protect it and retain it only as long as necessary for the purpose it was collected.",
  },
  "terms": {
    eyebrow: "Terms of use",
    heading: "Simple terms for using this site.",
    body: "The content on this website is provided for general information. You may use it for lawful, personal, and business purposes, but may not copy or misuse it in a way that infringes on our rights or the rights of others. Engagement terms for client work are agreed separately in writing.",
  },
} as const

type StaticPageId = keyof typeof staticPages
type PublicPageId =
  | "home"
  | "services"
  | "ai-strategy"
  | "web-development"
  | "design"
  | "product"
  | "sprint-zero"
  | "solutions"
  | "early-startups"
  | "growth-startups"
  | "small-medium-businesses"
  | "private-equity"
  | "accelerators"
  | "about"
  | "our-approach"
  | "our-team"
  | "contact"
  | "first-time-founders"
  | "privacy"
  | "terms"

const PageHeader = ({
  eyebrow,
  heading,
  body,
  accent,
  breakAfter,
}: {
  eyebrow: string
  heading: string
  body: string
  accent?: string
  breakAfter?: string
}) => {
  return (
    <section className="container marketing-hero">
      <div className="max-w-3xl">
        <p className="marketing-orange-text marketing-eyebrow">{eyebrow}</p>
        <h1 className="home-sans-heading marketing-page-title mt-4">
          <HeadlineText text={heading} accent={accent} breakAfter={breakAfter} />
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">{body}</p>
      </div>
    </section>
  )
}

const CallToAction = () => <ContactCta />

const RelatedWork = ({
  caseStudyIds,
  excludedCaseStudyIds = [],
  excludeFirstCaseStudy = true,
  includeFeaturedCaseStudy = false,
}: {
  caseStudyIds?: readonly import("~/content/cases").CaseStudyId[]
  excludedCaseStudyIds?: readonly import("~/content/cases").CaseStudyId[]
  excludeFirstCaseStudy?: boolean
  includeFeaturedCaseStudy?: boolean
}) => {
  if (!caseStudyIds || caseStudyIds.length === 0) return null

  const destination = workPath({})
  const [featuredCaseStudyId, ...relatedCaseStudyIds] = caseStudyIds
  const relatedWorkFallbacks: Partial<
    Record<import("~/content/cases").CaseStudyId, import("~/content/cases").CaseStudyId>
  > = {
    "brightlines": "quadrant",
    "hadley": "skillschool",
    "leapfund": "target100",
    "the-information": "brightlines",
    "dapper-labs": "the-information",
    "target100": "pearland",
    "quadrant": "target100",
  }
  const relatedWorkCandidates = [
    ...(includeFeaturedCaseStudy ? [featuredCaseStudyId] : []),
    ...relatedCaseStudyIds,
    relatedWorkFallbacks[featuredCaseStudyId],
    ...caseStudyIds,
    ...(Object.keys(caseStudies) as CaseStudyId[]),
  ].filter((id): id is CaseStudyId => Boolean(id))
  const relatedWorkCaseStudyIds = Array.from(new Set(relatedWorkCandidates))
    .filter(
      (id) =>
        !excludedCaseStudyIds.includes(id) && (!excludeFirstCaseStudy || id !== featuredCaseStudyId)
    )
    .slice(0, 3)

  return (
    <section id="related-work" className="bg-muted/40 marketing-section">
      <div className="container">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-3xl">
            <h2 className="home-sans-heading marketing-section-heading">Related work</h2>
            <p className="mt-5 text-lg leading-relaxed sm:text-xl">
              Explore how we&apos;ve helped teams solve similar product challenges.
            </p>
          </div>
          <Link
            to={destination}
            className="inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-primary"
            onClick={() => trackCtaClick("See all our work", "related-work", destination)}
          >
            See all our work <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
        <div className="work-index-grid mt-10">
          {relatedWorkCaseStudyIds.map((id, index) => (
            <CaseStudyCard key={id} id={id} priority={index === 0} />
          ))}
        </div>
      </div>
    </section>
  )
}

const solutionScheduleCtaLocations = {
  "early-startups": "solution-early-startups",
  "growth-startups": "solution-growth-startups",
  "small-medium-businesses": "solution-small-medium-businesses",
  "private-equity": "solution-private-equity",
  "accelerators": "solutions-get-started",
} as const satisfies Record<string, CalendlyCtaLocation>

const founderScheduleCtaLocations = {
  "david-renz": "contact-founder-david-renz",
  "eric-skiff": "contact-founder-eric-skiff",
} as const satisfies Record<string, CalendlyCtaLocation>

const defaultContactCta = {
  heading: "Let's talk about how we can help.",
  body: "We LOVE talking about ideas and how to apply tech to bring them to life. Whether you're ready to build tomorrow or exploring, we'll share our thoughts and help however we can.",
  scheduleCtaLocation: "solutions-get-started",
} as const satisfies { heading: string; body: string; scheduleCtaLocation: CalendlyCtaLocation }

export const SourceContactEndcap = ({
  cta,
  copy,
  workPage = false,
  showNewsletter = true,
}: {
  copy?: { heading: string; body: string }
  workPage?: boolean
  showNewsletter?: boolean
  cta?: { heading: string; body: string; label?: string; scheduleCtaLocation: CalendlyCtaLocation }
}) => (
  <>
    <section
      className="marketing-surface-dark marketing-section bg-cover bg-center text-secondary-foreground"
      style={{ backgroundImage: `url(${assetManifest["footer-background-hr"].source})` }}
    >
      <div className="container grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(28rem,1.1fr)] lg:gap-20">
        <div className="max-w-xl">
          <h2 className="home-sans-heading marketing-section-heading">
            {copy ? (
              copy.heading
            ) : workPage ? (
              "What are you working on?"
            ) : cta ? (
              cta.heading
            ) : (
              <>
                <span className="home-emphasis">Let&apos;s talk</span> about how we can help.
              </>
            )}
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-secondary-foreground/80 sm:text-xl">
            {copy
              ? copy.body
              : workPage
                ? "Tell us what you want to build or improve. We’ll talk through your goals and where to start."
                : cta
                  ? cta.body
                  : "We LOVE talking about ideas and how to apply tech to bring them to life. Whether you're ready to build tomorrow or exploring, we'll share our thoughts and help however we can."}
          </p>
          {cta && (
            <ScheduleLink
              ctaLocation={cta.scheduleCtaLocation}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground hover:bg-primary/90"
            >
              {cta.label ?? "Schedule a call"} <ArrowRight className="size-4" aria-hidden="true" />
            </ScheduleLink>
          )}
        </div>
        <div className="[&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary/90 [&_input]:border-0 [&_input]:bg-secondary [&_input]:text-secondary-foreground [&_label]:text-secondary-foreground [&_textarea]:border-0 [&_textarea]:bg-secondary [&_textarea]:text-secondary-foreground">
          <ContactForm submitLabel={workPage || copy ? "Let’s talk" : undefined} />
        </div>
      </div>
    </section>
    {showNewsletter && (
      <section className="marketing-surface-primary marketing-section bg-primary text-primary-foreground">
        <div className="container text-center">
          <h2 className="home-sans-heading marketing-section-heading mx-auto max-w-4xl">
            {workPage ? "Keep building with us." : <>Subscribe to our newsletter</>}
          </h2>
          <div className="mx-auto mt-7 max-w-md text-left [&_form]:mx-auto [&_button]:w-full [&_button]:rounded-full [&_button]:bg-secondary [&_button]:text-secondary-foreground [&_button]:hover:bg-secondary/90 [&_input]:border [&_input]:border-primary-foreground/35 [&_input]:bg-background [&_input]:focus-visible:ring-secondary [&_input]:focus-visible:ring-offset-primary [&_label]:sr-only">
            <NewsletterForm placement="page-endcap" />
          </div>
        </div>
      </section>
    )}
  </>
)

const servicePaths: Record<string, string> = {
  "ai-strategy": aiStrategyPath({}),
  "web-development": webDevelopmentPath({}),
  "design": designPath({}),
  "product-management": productPath({}),
  "sprint-zero": sprintZeroPath({}),
}

const serviceLinksByTitle: Record<string, string> = {
  "Web & Mobile Development": servicePaths["web-development"],
  "Sprint Zero: A Smarter Start": servicePaths["sprint-zero"],
  "Design": servicePaths["design"],
  "Product Management": servicePaths["product-management"],
  "AI Strategy & Development": servicePaths["ai-strategy"],
}

const serviceEvidence: Record<string, CaseStudyId> = {
  "ai-strategy": "brightlines",
  "web-development": "the-information",
  "design": "hadley",
  "product-management": "leapfund",
  "sprint-zero": "dapper-labs",
}

const ServiceCards = ({ items }: { items: readonly Service[] }) => (
  <div className="container grid gap-8 pb-20 md:grid-cols-2">
    {items.map((service, index) => {
      const caseStudy = caseStudies[serviceEvidence[service.slug]]
      return (
        <article
          key={service.slug}
          className="group overflow-hidden rounded-3xl border border-border bg-card"
        >
          <ResponsiveImage
            assetId={caseStudy.coverAssetId ?? caseStudy.heroImageAssetId!}
            alt={`${caseStudy.name} product interface`}
            className="aspect-[16/8] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            sizes="half"
            priority={index < 2}
          />
          <div className="p-7">
            <h3 className="home-sans-heading text-2xl font-bold tracking-tight md:font-normal md:leading-[1.4]">
              {service.overviewName ?? service.name}
            </h3>
            <p className="mt-4 text-muted-foreground md:max-w-[32.875rem] md:text-base md:leading-6">
              {service.overviewDescription ?? service.description}
            </p>
            <Link
              to={servicePaths[service.slug]}
              aria-label={`Learn More about ${service.overviewName ?? service.name}`}
              className="mt-6 inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-primary"
            >
              Learn More <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        </article>
      )
    })}
  </div>
)

const testimonials = {
  "ai-strategy": {
    quote:
      "The Tanooki Labs team was an absolute pleasure to work with and treated our product like their own. They gave a level of attention, commitment and quality I would expect from an internal team.",
    person: "Nick Kroetz",
    role: "Noodle Pros",
  },
  "design": {
    quote:
      "Tanooki Labs brought their passion and commitment to the process of building SkillSchool. Every week I looked forward to reviewing the process and doing the strategy work with the team to make SkillSchool a great experience for instructors.",
    person: "Bre Pettis",
    role: "Founder & CEO of SkillSchool",
  },
  "hadley": {
    quote:
      "Consultative doesn’t even scratch the surface of how collaborative this team is. As a first-time founder, I’m grateful for my partnership with Tanooki. They make it easy to adapt to my always-changing, always-stressful needs and situations.",
    person: "Yosh Miller",
    role: "Hadley",
  },
  "default": {
    quote:
      "The Tanooki team has the heart and guts of a start-up, and it's been invaluable to have them working alongside us. They get how crucial deadlines are, which is so important for an early-stage company that needs to bring in outside development.",
    person: "Daniela Perdomo",
    role: "GoTenna",
  },
} as const

const EvidenceQuote = ({ kind = "default" }: { kind?: keyof typeof testimonials }) => {
  const testimonial = testimonials[kind]
  return (
    <section className="bg-muted/40 marketing-section">
      <div className="container text-center">
        <blockquote className="mx-auto max-w-4xl text-balance text-2xl leading-snug sm:text-3xl">
          “{testimonial.quote}”
        </blockquote>
        <p className="mt-6 font-semibold">
          {testimonial.person}{" "}
          <span className="font-normal text-muted-foreground">· {testimonial.role}</span>
        </p>
      </div>
    </section>
  )
}

const DudaTestimonial = ({
  kind,
  source,
}: {
  kind: keyof typeof testimonials
  source?: {
    heading: string
    intro: string
    quote: string
    name: string
    role: string
  }
}) => {
  const testimonial = testimonials[kind]

  return (
    <section className="bg-muted/40 marketing-section">
      <div className="container text-center">
        {source && (
          <>
            <h2 className="home-sans-heading marketing-section-heading mx-auto max-w-4xl">
              {source.heading}
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
              {source.intro}
            </p>
          </>
        )}
        <blockquote
          className={cn(
            "mx-auto max-w-4xl text-balance text-2xl leading-snug sm:text-3xl",
            source && "mt-10"
          )}
        >
          “{source?.quote ?? testimonial.quote}”
        </blockquote>
        <p className="mt-6 font-semibold">
          {source?.name ?? testimonial.person}{" "}
          <span className="font-normal text-muted-foreground">
            · {source?.role ?? testimonial.role}
          </span>
        </p>
      </div>
    </section>
  )
}

const MetricStrip = ({ caseStudyId }: { caseStudyId: CaseStudyId }) => {
  const metric =
    caseStudyId === "dapper-labs"
      ? { value: "$1B+", label: "in NBA Top Shot transactions", id: "dapper-labs" as const }
      : caseStudyId === "the-information"
        ? {
            value: "10 weeks",
            label: "concept to launch for The Information",
            id: "the-information" as const,
          }
        : null
  const destination = metric ? workDetailPath({ slug: metric.id }) : workPath({})
  return (
    <section className="border-y border-border bg-background py-10">
      <div className="container">
        <Link to={destination} className="group block max-w-3xl">
          <p className="marketing-eyebrow text-muted-foreground">Results from our work</p>
          <p className="home-sans-heading mt-2 text-5xl sm:text-6xl">
            <span className="home-emphasis">{metric?.value ?? "200+"}</span>{" "}
            <span className="text-2xl sm:text-3xl">
              {metric?.label ?? "product launches over 12+ years"}
            </span>
          </p>
          <span className="mt-3 inline-flex items-center gap-2 font-semibold underline underline-offset-4 group-hover:text-primary">
            {metric ? "View case study" : "See our work"} <ArrowRight className="size-4" />
          </span>
        </Link>
      </div>
    </section>
  )
}

const CaseStudyFeature = ({ caseStudyIds }: { caseStudyIds: readonly CaseStudyId[] }) => {
  const caseStudy = caseStudies[caseStudyIds[0]]
  const image = caseStudy.coverAssetId ?? caseStudy.heroImageAssetId
  if (!image) return null
  return (
    <section className="marketing-section">
      <div className="container grid items-center gap-10 md:grid-cols-2 lg:gap-16">
        <ResponsiveImage
          assetId={image}
          alt={`${caseStudy.name} product interface`}
          className="aspect-[4/3] w-full rounded-3xl object-cover"
          sizes="half"
        />
        <div>
          <p className="marketing-eyebrow text-muted-foreground">Case study</p>
          <h2 className="home-sans-heading marketing-section-heading mt-3">
            <span className="home-emphasis">{caseStudy.name}</span>
          </h2>
          <p className="mt-5 text-xl leading-relaxed">{caseStudy.summary}</p>
          <Link
            to={workDetailPath({ slug: caseStudy.slug })}
            className="mt-7 inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-primary"
          >
            View case study <ArrowRight className="size-4" />
          </Link>
        </div>
      </div>
    </section>
  )
}

const whyCompaniesChooseTanooki = [
  {
    title: "Proven Track Record",
    body: "Proven track record with more than 200 product launches and a 91 percent success rate",
  },
  {
    title: "Onshore Senior Team",
    body: "Senior engineers, product strategists, and designers based in the US and Canada",
  },
  {
    title: "Agile, AI-Enabled",
    body: "AI-assisted development with weekly demos; we only use AI models that keep your proprietary data secure.",
  },
  {
    title: "Transparent, Collaborative",
    body: "Transparent, collaborative process with weekly demos and real-time Slack communication",
  },
] as const

const dudaHeroImages: Record<DudaCopyPageId, keyof typeof assetManifest> = {
  "services": "case2-theinformation",
  "ai-strategy": "reportable-feature2",
  "web-development": "case2-theinformation",
  "design": "hadley-feature",
  "product": "dapper-feature-2",
  "sprint-zero": "hadley-feature",
  "solutions": "hadley-feature",
  "early-startups": "hadley-feature",
  "growth-startups": "dapper-feature-2",
  "small-medium-businesses": "t100-feature-2",
  "private-equity": "quadrant-mockup",
  "accelerators": "ss-3",
}

const dudaRelatedWork: Partial<Record<DudaCopyPageId, readonly CaseStudyId[]>> = {
  "services": ["dapper-labs", "hadley", "brightlines"],
  "ai-strategy": ["brightlines", "leapfund"],
  "web-development": ["the-information", "dapper-labs"],
  "design": ["hadley", "skillschool"],
  "product": ["dapper-labs", "skillschool"],
  "sprint-zero": ["hadley", "dapper-labs"],
  "early-startups": ["hadley", "skillschool"],
  "growth-startups": ["dapper-labs"],
  "small-medium-businesses": ["target100", "quadrant"],
  "private-equity": ["quadrant"],
  "accelerators": ["skillschool", "hadley"],
}

const dudaTestimonialKinds = {
  "services": "hadley",
  "ai-strategy": "ai-strategy",
  "web-development": "design",
  "design": "default",
  "product": "design",
  "sprint-zero": "hadley",
  "small-medium-businesses": "default",
  "private-equity": "ai-strategy",
  "accelerators": "hadley",
} as const satisfies Partial<Record<DudaCopyPageId, keyof typeof testimonials>>

const dudaShownCaseStudyIds: Partial<Record<DudaCopyPageId, readonly CaseStudyId[]>> = {
  "small-medium-businesses": ["target100", "quadrant", "obe-fitness"],
  "accelerators": ["the-information", "hadley", "dapper-labs"],
}

const serviceHeroEyebrows: Partial<Record<DudaCopyPageId, string>> = {
  "services": "Services",
  "ai-strategy": "AI Strategy & Development",
  "sprint-zero": "Sprint Zero",
}

const DudaHero = ({
  heading,
  lead,
  image,
  ctaLocation,
  eyebrow = "Solutions",
  imageAlt = "Tanooki Labs project work",
}: {
  heading: string
  lead: readonly string[]
  image: keyof typeof assetManifest
  ctaLocation: CalendlyCtaLocation
  eyebrow?: string
  imageAlt?: string
}) => (
  <section className="container marketing-hero marketing-hero-split">
    <div className="max-w-4xl" data-solution-hero-copy>
      <p className="marketing-orange-text marketing-eyebrow">{eyebrow}</p>
      <h1 className="home-sans-heading marketing-page-title marketing-split-title mt-4">
        {eyebrow === "Services" ? (
          <>
            <HeadlineText
              text={heading}
              accent="Great Products"
              breakAfter="Building Great Products"
            />
          </>
        ) : (
          heading
        )}
      </h1>
      <div className="mt-6 space-y-4 text-lg leading-relaxed text-muted-foreground sm:text-xl">
        {lead.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <ScheduleLink
          ctaLocation={ctaLocation}
          className="inline-flex min-h-11 items-center justify-center rounded-full bg-primary px-6 text-[18.66px] font-semibold text-primary-foreground hover:bg-primary/90"
          ariaLabel="Schedule a call"
        >
          Schedule a call <ArrowRight className="ml-2 size-4" aria-hidden="true" />
        </ScheduleLink>
        <a
          href="#related-work"
          className="inline-flex min-h-11 items-center justify-center rounded-full border border-border px-6 font-semibold hover:border-primary hover:text-primary"
        >
          See related work
        </a>
      </div>
    </div>
    <ResponsiveImage
      assetId={image}
      alt={imageAlt}
      className="aspect-[4/3] w-full rounded-3xl object-cover md:col-start-2 md:row-start-1"
      sizes="half"
      priority
    />
  </section>
)

const SolutionProofRow = () => (
  <section className="border-y border-border bg-muted/30 py-8 sm:py-10">
    <div className="container grid gap-6 sm:grid-cols-3">
      {[
        ["200+", "product launches"],
        ["91%", "of clients still operating or acquired"],
        ["US & Canada", "senior team"],
      ].map(([value, label]) => (
        <div key={value}>
          <p className="home-sans-heading text-4xl font-bold leading-none tracking-tight sm:text-5xl">
            {value}
          </p>
          <p className="mt-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
        </div>
      ))}
    </div>
  </section>
)

const DudaCaseStudyCards = ({
  items,
}: {
  items: readonly { id: CaseStudyId; title: string; tags: readonly string[] }[]
}) => (
  <div className="work-index-grid mt-10">
    {items.map((item, index) => (
      <CaseStudyCard
        key={item.id}
        id={item.id}
        priority={index === 0}
        title={item.title}
        summary={item.tags.join(", ")}
        tags={item.tags}
      />
    ))}
  </div>
)

const DudaHeading = ({ children, className }: { children: string; className?: string }) => {
  return (
    <h2 className={cn("home-sans-heading text-4xl leading-tight sm:text-5xl", className)}>
      {children}
    </h2>
  )
}

const DudaChecklist = ({ items }: { items: readonly string[] }) => (
  <ul className="mt-8 grid gap-4 text-lg leading-relaxed sm:grid-cols-2">
    {items.map((item) => (
      <li key={item} className="flex gap-3">
        <Check className="mt-1 size-5 shrink-0 text-primary" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
)

const DudaCards = ({
  items,
  columns = "md:grid-cols-2",
  proofCard = false,
}: {
  items: readonly { title: string; body: string }[]
  columns?: string
  proofCard?: boolean
}) => (
  <div className={cn("mt-10 grid gap-5", columns)}>
    {items.map((item, index) => {
      const Icon = [Compass, Lightbulb, Rocket, Sparkles, CircleDot, Users][index % 6]
      return (
        <article
          key={item.title}
          data-marketing-card
          data-solution-proof-card={proofCard || undefined}
          className="rounded-3xl border border-border bg-card p-6 text-card-foreground sm:p-7"
        >
          <Icon className="size-6 text-primary" aria-hidden="true" />
          <h3 className="home-sans-heading mt-5 text-2xl leading-tight">{item.title}</h3>
          <p className="mt-3 leading-relaxed text-card-foreground/80">{item.body}</p>
        </article>
      )
    })}
  </div>
)

const DudaSolutionsPage = () => {
  const segments = [
    {
      heading: "Solutions for Early-Stage Startups and Non-Technical Founders",
      tagline: "Helping Businesses Turn Ideas Into Scalable Technology Products",
      body: "Bringing an idea to life is challenging without a technical co-founder. We help non-technical founders define their vision, build an MVP, and launch scalable products with a structured product design and agile development process.",
      items: [
        "Sprint Zero\nStructured product discovery and design before development begins",
        "MVP Development\nGet to market quickly with a lean, scalable product",
        "Fractional CTO Support\nGain technical guidance without hiring full-time",
      ],
      link: "Learn More",
      to: earlyStartupsPath({}),
      image: "hadley-feature",
    },
    {
      heading: "Growth-Stage Startups",
      tagline: "Expand Your Execution Power Without Disrupting Your Roadmap",
      body: "Fast-growing startups need additional engineering bandwidth to tackle high-impact projects while their core team focuses on the company’s vision. We act as an external development team, accelerating critical initiatives without the need for internal expansion.",
      items: [
        "Build revenue-generating features and new digital experiences",
        "Experiment with AI, automation, and integrations",
        "Develop internal tools and infrastructure to improve efficiency",
      ],
      link: "See How We Help Growth Startups Scale",
      to: growthStartupsPath({}),
      image: "dapper-feature-2",
    },
    {
      heading: "Small and Medium-Sized Businesses (SMBs)",
      tagline: "Move Critical Projects from Someday to Done",
      body: "SMBs often struggle to execute technology projects due to limited internal resources. We help these companies streamline operations, improve customer experiences, and implement AI-driven efficiencies with cost-effective, scalable solutions.",
      items: [
        "Custom software and AI solutions for business operations",
        "Seamless integration with existing teams and workflows",
        "Create technology products for new or existing lines of business",
      ],
      link: "Discover How We Help SMBs Deliver",
      to: smallMediumBusinessesPath({}),
      image: "t100-feature-2",
    },
    {
      heading: "Private Equity and Investment Firms",
      tagline: "Build Smart, Scalable Technology to Power Your Portfolio",
      body: "We work with private equity firms and their portfolio companies to modernize legacy systems, build AI-driven data solutions, and create custom investment management platforms that drive efficiency and growth.",
      items: [
        "Technical due diligence",
        "Roadmap planning for integration and growth",
        "Scalable software teams for portfolio companies",
      ],
      link: "Explore Our Work with Finance Firms",
      to: privateEquityPath({}),
      image: "quadrant-mockup",
    },
    {
      heading: "Accelerator Programs and Startup Incubators",
      tagline: "Supporting the Next Generation of Startups",
      body: "We partner with accelerators and incubators to provide technical guidance, MVP development, and product validation for their startups. Our structured product development process helps founders move from idea to execution with confidence.",
      items: [
        "Sprint Zero: Helping startups define and validate product ideas",
        "MVP and prototype development to attract investors",
        "Fractional CTO support and mentorship for accelerator cohorts",
      ],
      link: "See How We Support Startup Accelerators",
      to: acceleratorsPath({}),
      image: "ss-3",
    },
  ] as const

  return (
    <>
      <DudaHero
        heading="Technology Solutions for Every Stage of Growth"
        lead={[
          "Every company faces unique challenges when developing and scaling digital products. At Tanooki Labs, we have worked with companies of all sizes—from pre-product startups, SMBs, private equity, and accelerator programs—to build custom software tailored to their needs.",
          "Whether you need to launch an MVP, scale your product, or implement AI-driven efficiencies, we adapt our approach to meet your specific business goals.",
        ]}
        image="hadley-feature"
        ctaLocation="solutions-get-started"
      />
      <BrandBand />
      {segments.map((segment, index) => (
        <section
          key={segment.heading}
          className={cn("marketing-section", index % 2 === 1 && "bg-muted/40")}
        >
          <div className="container grid items-center gap-10 md:grid-cols-2 lg:gap-16">
            <ResponsiveImage
              assetId={segment.image}
              alt={`${segment.heading} project work`}
              className={cn(
                "aspect-[4/3] w-full rounded-3xl object-cover",
                index % 2 === 1 && "md:order-2"
              )}
              sizes="half"
            />
            <div>
              <DudaHeading>{segment.heading}</DudaHeading>
              <p className="mt-5 text-xl font-semibold leading-snug sm:text-2xl">
                {segment.tagline}
              </p>
              <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{segment.body}</p>
              <DudaChecklist items={segment.items} />
              <Link
                to={segment.to}
                className="mt-8 inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-primary"
              >
                {segment.link} <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      ))}
      <section className="marketing-surface-dark marketing-section text-secondary-foreground">
        <div className="container">
          <DudaHeading>Why Companies Choose Tanooki Labs</DudaHeading>
          <DudaCards
            columns="md:grid-cols-2 xl:grid-cols-4"
            proofCard
            items={[
              {
                title: "Proven Track Record",
                body: "Proven track record with more than 200 product launches and a 91 percent success rate",
              },
              {
                title: "Onshore Senior Team",
                body: "Senior engineers, product strategists, and designers based in the US and Canada",
              },
              {
                title: "Agile, AI-Enabled",
                body: "We only use AI models that ensure your proprietary data remains secure.",
              },
              {
                title: "Transparent, Collaboration",
                body: "Transparent, collaborative process with weekly demos and real-time Slack communication",
              },
            ]}
          />
        </div>
      </section>
      <EvidenceQuote kind="design" />
      <RelatedWork caseStudyIds={["skillschool", "hadley", "dapper-labs"]} />
      <SourceContactEndcap
        cta={{
          heading: "Get Started Today",
          body: "Schedule a free 30-minute consultation to discuss your project and explore the best approach for your business.",
          label: "Contact Us",
          scheduleCtaLocation: "solutions-get-started",
        }}
      />
    </>
  )
}

const DudaEarlyStartupsPage = () => (
  <>
    <DudaHero
      heading="From Idea to Product: Build Your Vision with Confidence"
      lead={[
        "You have a groundbreaking idea and deep expertise in your industry. What you don’t have—yet—is the technical team to bring it to life. That’s where Tanooki Labs comes in. We partner with non-technical founders to turn bold ideas into fully functional digital products.",
      ]}
      image="hadley-feature"
      ctaLocation="solution-early-startups"
    />
    <SolutionProofRow />
    <BrandBand />
    <section className="marketing-section">
      <div className="container">
        <div className="max-w-3xl">
          <DudaHeading>Navigating the Challenges of Early-Stage Startups</DudaHeading>
          <p className="mt-5 text-lg leading-relaxed sm:text-xl">
            Bringing a startup to life isn’t just about the idea—it’s about execution. Many
            early-stage founders face similar hurdles when it comes to technology:
          </p>
        </div>
        <DudaCards
          proofCard
          items={[
            {
              title: "Clear Dev Plan",
              body: "You need a clear development plan but aren’t sure how to translate your vision into a working product.",
            },
            {
              title: "Technical Co-founder",
              body: "You don’t have a technical co-founder to guide decisions on architecture, features, and timelines.",
            },
            {
              title: "Validate Quickly",
              body: "You need to validate your idea quickly with an MVP to attract investors or early customers.",
            },
            {
              title: "Experienced Partner",
              body: "You need an experienced development partner that understands startups and won’t waste time or budget on the wrong approach.",
            },
          ]}
        />
        <div className="mt-8 max-w-3xl space-y-4 text-lg leading-relaxed text-muted-foreground">
          <p>If any of these challenges sound familiar, you’re not alone.</p>
          <p>
            We help founders like you move from concept to launch with a structured, low-risk
            process.
          </p>
        </div>
      </div>
    </section>
    <section className="marketing-surface-dark marketing-section text-secondary-foreground">
      <div className="container">
        <div className="max-w-3xl">
          <DudaHeading>How Tanooki Labs Helps Founders Build Smart</DudaHeading>
          <p className="mt-5 text-lg leading-relaxed sm:text-xl">
            We provide the technical foundation and expertise needed to transform your idea into a
            market-ready product.
          </p>
          <h3 className="home-sans-heading mt-10 text-3xl leading-tight sm:text-4xl">
            Sprint Zero: Laying the Right Foundation
          </h3>
          <p className="mt-5 text-lg leading-relaxed">
            Our Sprint Zero process ensures that your startup is built on a clear strategy,
            validated assumptions, and a realistic roadmap before development begins.
          </p>
        </div>
        <DudaCards
          proofCard
          items={[
            {
              title: "Business & User Discovery",
              body: "We align on your target users, market needs, and competitive landscape.",
            },
            {
              title: "Product Definition",
              body: "We define user journeys, key features, and technical requirements.",
            },
            {
              title: "Branding & UX/UI Design",
              body: "We create wireframes, visual identity, and interactive prototypes.",
            },
            {
              title: "Technical Planning",
              body: "We assess development platforms, integrations, and scalability needs.",
            },
            {
              title: "Roadmap & Cost Estimate",
              body: "We deliver a timeline and budget that sets realistic expectations.",
            },
          ]}
        />
      </div>
    </section>
    <section className="marketing-section">
      <div className="container">
        <div className="max-w-3xl">
          <DudaHeading>Agile Development: Iterative, Transparent, and Scalable</DudaHeading>
          <p className="mt-5 text-lg leading-relaxed sm:text-xl">
            Once Sprint Zero is complete, we move into Agile development sprints, ensuring
            continuous progress and real-time visibility.
          </p>
        </div>
        <DudaCards
          proofCard
          items={[
            {
              title: "Weekly Planning & Standups",
              body: "Stay informed with regular updates and collaborative planning.",
            },
            {
              title: "Rapid Iteration & Testing",
              body: "Get working features early and often to gather user feedback.",
            },
            {
              title: "Scalability from Day One",
              body: "We build technology that can grow with your startup.",
            },
            {
              title: "Dedicated Slack Channel",
              body: "Direct access to our team for seamless collaboration.",
            },
          ]}
        />
      </div>
    </section>
    <section className="marketing-surface-dark marketing-section text-secondary-foreground">
      <div className="container">
        <div className="max-w-3xl">
          <DudaHeading>AI-Enabled Development: Build Smarter, Faster</DudaHeading>
          <p className="mt-5 text-lg leading-relaxed sm:text-xl">
            AI is changing how startups build products. We integrate AI strategically to help you
            get to market faster without unnecessary costs.
          </p>
        </div>
        <DudaCards
          items={[
            {
              title: "AI-powered development tools",
              body: "to streamline coding and reduce development time.",
            },
            {
              title: "Automated customer interactions",
              body: "via AI chatbots and support tools.",
            },
            { title: "Data-driven Insights", body: "help you make informed product decisions." },
          ]}
          columns="md:grid-cols-3"
        />
      </div>
    </section>
    <section className="marketing-section">
      <div className="container">
        <div className="max-w-3xl">
          <DudaHeading>Case Studies: Startups We&apos;ve Helped Launch</DudaHeading>
          <p className="mt-5 text-lg leading-relaxed sm:text-xl">
            We’ve worked with early-stage startups across industries—helping founders turn concepts
            into successful, scalable products.
          </p>
        </div>
        <DudaCaseStudyCards
          items={[
            {
              id: "the-information",
              title: "The Information",
              tags: [
                "Mobile App",
                "Sprint Zero",
                "Web",
                "React Native",
                "iOS",
                "Android",
                "Design",
              ],
            },
            {
              id: "hadley",
              title: "Hadley",
              tags: ["Financial Services", "Design", "Sprint Zero", "Web"],
            },
            { id: "dapper-labs", title: "Dapper Labs", tags: ["Developers Helping Developers"] },
          ]}
        />
        <p className="mt-8 max-w-3xl text-lg leading-relaxed text-muted-foreground">
          From fintech to social apps, our team has helped founders launch and scale products that
          make an impact.
        </p>
      </div>
    </section>
    <section className="bg-muted/40 marketing-section">
      <div className="container">
        <div className="max-w-3xl">
          <DudaHeading>Why Founders Choose Tanooki Labs</DudaHeading>
          <p className="mt-5 text-lg leading-relaxed sm:text-xl">
            With 12+ years of experience and 200+ product launches, we know what it takes to build
            startups the right way.
          </p>
        </div>
        <DudaCards
          items={[
            {
              title: "No In-House Tech Team Needed",
              body: "We serve as your fractional CTO and development team, guiding you from idea to execution.",
            },
            {
              title: "Onshore Startup-Experienced Team",
              body: "Our US & Canada-based engineers have deep experience working with startups.",
            },
            {
              title: "Sprint Zero De-risks Development",
              body: "We ensure your product is well-defined before writing a single line of code.",
            },
            {
              title: "Flexible Engagements",
              body: "Whether you need an MVP, a full-scale product, or ongoing support, we adapt to your needs.",
            },
          ]}
        />
      </div>
    </section>
    <EvidenceQuote kind="hadley" />
    <RelatedWork
      caseStudyIds={["skillschool", "target100", "brightlines"]}
      excludedCaseStudyIds={["the-information", "hadley", "dapper-labs"]}
      excludeFirstCaseStudy={false}
    />
    <SourceContactEndcap
      cta={{
        heading: "Get Started Today",
        body: "Schedule a free 30-minute consultation to discuss your project and explore the best approach for your business.",
        label: "Contact Us",
        scheduleCtaLocation: "solutions-get-started",
      }}
    />
  </>
)

const DudaGrowthStartupsPage = () => (
  <>
    <DudaHero
      heading="Expand Your Execution Power Without Disrupting Your Roadmap"
      lead={[
        "Your internal tech and product teams are focused on delivering your company’s core roadmap—but what about the other critical projects that keep getting pushed to the back burner?",
        "At Tanooki Labs, we act as your trusted external development team, executing high-impact initiatives without diverting your internal resources.",
      ]}
      image="dapper-feature-2"
      ctaLocation="solution-growth-startups"
    />
    <SolutionProofRow />
    <BrandBand />
    <section className="marketing-section">
      <div className="container [&>*]:max-w-5xl">
        <DudaHeading>We Solve the &quot;Not Enough Bandwidth&quot; Problem</DudaHeading>
        <p className="mt-5 text-lg leading-relaxed sm:text-xl">
          As a growing startup, your engineering and product teams are locked in on your company’s
          vision. But what happens when other essential projects arise?
        </p>
        <p className="mt-5 text-lg leading-relaxed">This is where we come in.</p>
        <DudaChecklist
          items={[
            "A new revenue-driving initiative needs to be built, but your team can’t shift focus.",
            "Innovation projects to explore how a new technology can be applied to the business.",
            "You want to explore AI, automation, or integrations, but don’t have the bandwidth for R&D.",
            "A custom internal tool could streamline operations, but no one has time to build it.",
          ]}
        />
      </div>
    </section>
    <section className="marketing-surface-dark marketing-section text-secondary-foreground">
      <div className="container">
        <div className="max-w-3xl">
          <DudaHeading>A Fully Autonomous Team You Can Trust</DudaHeading>
          <p className="mt-5 text-lg leading-relaxed sm:text-xl">
            We integrate seamlessly into your workflow, requiring minimal oversight from your
            internal team. Our structured Sprint Zero process ensures we deeply understand your
            business needs upfront, allowing us to deliver results without constant check-ins or
            hand-holding.
          </p>
        </div>
        <DudaCards
          items={[
            {
              title: "We self-manage",
              body: "Our senior engineers and product managers drive projects forward independently.",
            },
            {
              title: "We fit into your workflow",
              body: "Whether you use Slack, Jira, Linear, or another system, we work within your environment.",
            },
            {
              title: "We deliver high-quality results, fast",
              body: "Our Agile sprints and weekly demos keep things moving smoothly.",
            },
          ]}
          columns="md:grid-cols-3"
        />
      </div>
    </section>
    <section className="marketing-section">
      <div className="container">
        <div className="max-w-3xl">
          <DudaHeading>How We Help Later-Stage Startups Execute Faster</DudaHeading>
        </div>
        <DudaCards
          items={[
            {
              title: "Build Revenue-Driving Features",
              body: "Develop customer-facing tools, partner integrations, or new digital experiences without pulling engineers away from core product development.",
            },
            {
              title: "Experiment with AI and Automation",
              body: "Explore AI-powered features, workflow automation, and predictive analytics to enhance user experiences and internal processes.",
            },
            {
              title: "Develop Internal Tools & Infrastructure",
              body: "Build internal platforms, dashboards, and operational tools that improve efficiency without requiring internal development resources.",
            },
            {
              title: "Support Marketing & Growth Initiatives",
              body: "Create interactive demo applications, community portals, sales enablement tools, and custom software to support marketing campaigns.",
            },
          ]}
        />
      </div>
    </section>
    <section className="bg-muted/40 marketing-section">
      <div className="container">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold tracking-widest text-muted-foreground">
            Case Studies: How We&apos;ve Helped Growth-Stage Startups
          </p>
          <DudaHeading className="mt-3">
            Dapper Labs: Enhancing Developer Experience to Drive Blockchain Adoption
          </DudaHeading>
          <p className="mt-5 text-lg leading-relaxed sm:text-xl">
            We’ve worked with later-stage startups that needed an external partner to move fast on
            high-priority projects.
          </p>
        </div>
        <div className="mt-10 grid overflow-hidden rounded-3xl border border-border bg-card text-card-foreground md:grid-cols-2">
          <ResponsiveImage
            assetId="dapper-feature-2"
            alt="Dapper Labs Flow tools"
            className="h-full min-h-72 w-full object-cover"
            sizes="half"
          />
          <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-14">
            <p className="text-lg leading-relaxed">
              Dapper Labs, the creators of Flow blockchain, wanted to accelerate adoption by
              improving how developers interacted with their platform. Their developer experience
              team partnered with Tanooki Labs to create tools and resources that made it easier for
              developers to build applications on Flow.
            </p>
            <p className="mt-5 font-semibold">Our work focused on three key areas:</p>
            <DudaChecklist
              items={[
                "Reference Applications – We built real-world example applications showcasing best practices for building on Flow. These reference apps provided developers with clear, working implementations they could learn from and modify for their own use cases.",
                "Developer Tools – We created essential tools to streamline the development process, making it easier to integrate Flow into new applications and reducing friction in onboarding.",
                "New Documentation Site – We designed and developed a documentation hub with better structure, clearer guides, and interactive components, ensuring developers had everything they needed to get started quickly and troubleshoot efficiently.",
              ]}
            />
            <p className="mt-6 text-lg leading-relaxed">
              By enhancing the developer experience, we helped Dapper Labs reduce barriers to entry,
              making Flow more accessible and attractive to new developers. These improvements not
              only drove platform adoption but also expanded the ecosystem of applications built on
              Flow, reinforcing its position as a leading blockchain for NFTs and decentralized
              applications.
            </p>
            <Link
              to={workDetailPath({ slug: "dapper-labs" })}
              className="mt-7 inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-primary"
            >
              View Case Study <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
    <section className="py-16 sm:py-24">
      <div className="container">
        <div className="max-w-3xl">
          <DudaHeading>Why Later-Stage Startup Choose Tanooki Labs</DudaHeading>
        </div>
        <DudaCards
          proofCard
          items={[
            {
              title: "A True Extension of Your Team",
              body: "We collaborate without adding management overhead.",
            },
            {
              title: "Deep Technical Expertise",
              body: "Our senior engineers build scalable, maintainable solutions.",
            },
            {
              title: "Fast, Iterative Execution",
              body: "Agile sprints ensure continuous progress with frequent demos.",
            },
            {
              title: "AI-Enabled Development",
              body: "We leverage AI tools to speed up development while protecting your IP.",
            },
            {
              title: "Onshore, Experienced Team",
              body: "US & Canada-based engineers, designers, and product strategists.",
            },
          ]}
        />
      </div>
    </section>
    <EvidenceQuote kind="ai-strategy" />
    <RelatedWork caseStudyIds={["dapper-labs", "brightlines", "reportable-news"]} />
    <SourceContactEndcap
      cta={{
        heading: "Let's Make It Happen",
        body: "Your team is doing incredible work. We’re here to help you move faster without compromise. Schedule a Consultation to discuss your project needs and see how we can help.",
        label: "Contact Us",
        scheduleCtaLocation: "solutions-get-started",
      }}
    />
  </>
)

const DudaCopyPage = ({ page }: { page: DudaCopyPageId }) => {
  if (page === "solutions") return <DudaSolutionsPage />
  if (page === "early-startups") return <DudaEarlyStartupsPage />
  if (page === "growth-startups") return <DudaGrowthStartupsPage />
  const content = marketingPages[page]
  const relatedWork = dudaRelatedWork[page]
  const icons = [Compass, Lightbulb, Rocket, Sparkles, CircleDot, Users]
  const gridClassName = (itemCount: number) => {
    if (itemCount === 2) return "md:grid-cols-2"
    if (itemCount === 3) return "md:grid-cols-3"
    if (itemCount === 4) return "md:grid-cols-2"
    if (itemCount === 5)
      return "md:grid-cols-2 xl:grid-cols-6 xl:[&>article]:col-span-2 xl:[&>article:nth-child(4)]:col-start-2"
    if (itemCount === 6) return "md:grid-cols-2 xl:grid-cols-3"
    if (itemCount >= 7 && itemCount % 3 === 1)
      return "md:grid-cols-2 xl:grid-cols-6 xl:[&>article]:col-span-2 xl:[&>article:last-child]:col-start-3"
    if (itemCount >= 7 && itemCount % 3 === 2)
      return "md:grid-cols-2 xl:grid-cols-6 xl:[&>article]:col-span-2 xl:[&>article:nth-last-child(2)]:col-start-2"
    return "md:grid-cols-2 xl:grid-cols-3"
  }

  return (
    <>
      <DudaHero
        heading={content.hero.h1}
        lead={content.hero.lead}
        image={dudaHeroImages[page]}
        eyebrow={content.hero.eyebrow ?? serviceHeroEyebrows[page] ?? "Solutions"}
        imageAlt={page === "sprint-zero" ? "Hadley product interface" : undefined}
        ctaLocation="solutions-get-started"
      />
      <BrandBand />
      {page === "private-equity" && (
        <section className="marketing-surface-dark marketing-section text-secondary-foreground">
          <div className="container">
            <div className="max-w-3xl">
              <h2 className="home-sans-heading marketing-section-heading">
                {content.sections[1].heading}
              </h2>
              {content.sections[1].intro?.map((paragraph) => (
                <p key={paragraph} className="mt-5 text-lg leading-relaxed sm:text-xl">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-10 grid gap-5 lg:grid-cols-2">
              {content.sections.slice(2, 4).map((section) => (
                <article
                  key={section.heading}
                  className="rounded-3xl border border-card-foreground/15 bg-card p-6 text-card-foreground sm:p-7"
                >
                  <h3 className="home-sans-heading text-2xl leading-tight">{section.heading}</h3>
                  {section.intro?.map((paragraph) => (
                    <p
                      key={paragraph}
                      className="mt-3 text-sm leading-relaxed text-card-foreground/80"
                    >
                      {paragraph}
                    </p>
                  ))}
                  <div className="mt-6 space-y-4">
                    {section.items.map((item) => (
                      <div key={item.title}>
                        <h4 className="font-semibold">{item.title}</h4>
                        <p className="mt-1 text-sm leading-relaxed text-card-foreground/80">
                          {item.body}
                        </p>
                      </div>
                    ))}
                  </div>
                  {section.outro && (
                    <p className="mt-6 text-sm leading-relaxed text-card-foreground/80">
                      {section.outro}
                    </p>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
      {content.sections
        .filter((_, index) => page !== "private-equity" || ![1, 2, 3].includes(index))
        .map((section, sectionIndex) => (
          <section
            key={`${section.heading}-${sectionIndex}`}
            className={cn(
              "marketing-section",
              (section.tone === "dark" || section.kind === "steps") &&
                "marketing-surface-dark text-secondary-foreground"
            )}
          >
            <div className="container">
              <div className="max-w-3xl">
                <h2 className="home-sans-heading marketing-section-heading">{section.heading}</h2>
                {section.intro?.map((paragraph, index) => (
                  <p
                    key={paragraph}
                    className={cn("text-lg leading-relaxed sm:text-xl", index === 0 && "mt-5")}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
              {section.items.length > 0 && (
                <div className={cn("mt-10 grid gap-5", gridClassName(section.items.length))}>
                  {section.items.map((item, itemIndex) => {
                    const Icon = icons[itemIndex % icons.length]
                    return (
                      <article
                        key={`${item.title}-${itemIndex}`}
                        data-marketing-card={
                          section.kind === "cards" || section.kind === "steps" || undefined
                        }
                        className={cn(
                          "rounded-3xl border bg-card p-6 text-card-foreground",
                          section.tone === "dark" ? "border-card-foreground/15" : "border-border"
                        )}
                      >
                        <div className="flex items-start gap-4">
                          {section.kind === "steps" ? (
                            <span
                              aria-hidden="true"
                              className="mt-4 h-0.5 w-5 shrink-0 bg-primary"
                            />
                          ) : section.kind === "checklist" ? (
                            <Check
                              className="mt-1 size-5 shrink-0 text-primary"
                              aria-hidden="true"
                            />
                          ) : (
                            <Icon
                              className="mt-1 size-6 shrink-0 text-primary"
                              aria-hidden="true"
                            />
                          )}
                          <div>
                            <h3 className="home-sans-heading text-2xl leading-tight">
                              {item.title}
                            </h3>
                            {item.body && (
                              <p className="mt-3 text-sm leading-relaxed text-card-foreground/80">
                                {item.body}
                              </p>
                            )}
                            {item.learnMore && serviceLinksByTitle[item.title] && (
                              <Link
                                to={serviceLinksByTitle[item.title]}
                                aria-label={`Learn More about ${item.title}`}
                                className="mt-4 inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-primary"
                              >
                                Learn More <ArrowRight className="size-4" aria-hidden="true" />
                              </Link>
                            )}
                            {item.checklist && (
                              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-card-foreground/80">
                                {item.checklist.map((checklistItem) => (
                                  <li key={checklistItem} className="flex gap-2">
                                    <Check
                                      className="mt-0.5 size-4 shrink-0 text-primary"
                                      aria-hidden="true"
                                    />
                                    <span>{checklistItem}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </article>
                    )
                  })}
                </div>
              )}
              {section.outro && (
                <p className="mt-8 max-w-3xl text-lg leading-relaxed sm:text-xl">{section.outro}</p>
              )}
            </div>
          </section>
        ))}
      {content.featuredCase && (
        <section className="marketing-section">
          <div className="container grid overflow-hidden rounded-3xl border border-border bg-card md:grid-cols-2">
            <ResponsiveImage
              assetId={
                caseStudies[content.featuredCase.caseStudyId].coverAssetId ??
                caseStudies[content.featuredCase.caseStudyId].heroImageAssetId!
              }
              alt={`${content.featuredCase.name} product interface`}
              className="h-full min-h-72 w-full object-cover"
              sizes="half"
            />
            <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-14">
              <p className="marketing-eyebrow text-muted-foreground">Case study</p>
              <h2 className="home-sans-heading marketing-section-heading mt-3">
                {content.featuredCase.name}
              </h2>
              {content.featuredCase.details ? (
                <div className="mt-6 space-y-5 text-lg leading-relaxed text-muted-foreground">
                  {content.featuredCase.details.map((detail) => (
                    <div key={detail.label}>
                      <p>
                        <strong className="text-foreground">{detail.label}:</strong>
                        {detail.body && ` ${detail.body}`}
                      </p>
                      {detail.items && (
                        <ul className="mt-2 space-y-2">
                          {detail.items.map((item) => (
                            <li key={item} className="flex gap-2">
                              <Check
                                className="mt-1 size-4 shrink-0 text-primary"
                                aria-hidden="true"
                              />
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-5 text-xl leading-relaxed">{content.featuredCase.summary}</p>
              )}
              <Link
                to={workDetailPath({ slug: caseStudies[content.featuredCase.caseStudyId].slug })}
                className="mt-7 inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-primary"
              >
                View Case Study <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      )}
      {content.restoredLines && (
        <section className="container py-10 sm:py-14">
          <div className="max-w-3xl space-y-4 text-lg leading-relaxed">
            {content.restoredLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        </section>
      )}
      {dudaTestimonialKinds[page] && (
        <DudaTestimonial kind={dudaTestimonialKinds[page]} source={content.testimonial} />
      )}
      {relatedWork && (
        <RelatedWork
          caseStudyIds={relatedWork}
          excludedCaseStudyIds={[
            ...(content.featuredCase ? [content.featuredCase.caseStudyId] : []),
            ...(dudaShownCaseStudyIds[page] ?? []),
          ]}
          excludeFirstCaseStudy={false}
          includeFeaturedCaseStudy={page === "services"}
        />
      )}
      <SourceContactEndcap
        cta={
          content.cta ? { ...content.cta, scheduleCtaLocation: "solutions-get-started" } : undefined
        }
      />
    </>
  )
}

const HomePage = () => <LandingPage />
export const ServicesPage = () => {
  return (
    <>
      <section className="container max-md:px-6 max-md:pt-7 pb-14 pt-14 md:pb-28 md:pt-32">
        <div>
          <h1 className="home-sans-heading max-w-[39rem] text-5xl leading-[1.08] tracking-tight md:max-w-[48rem] md:text-[3.75rem] md:leading-[1.1]">
            Build the right product, then keep it moving
          </h1>
          <p className="mt-5 max-w-[54rem] text-xl leading-snug text-foreground max-md:max-w-[20rem] md:mt-4 md:max-w-[46rem] md:text-2xl">
            Strategy, design, product management and engineering for teams with real work to ship.
          </p>
        </div>
      </section>
      <section className="container max-md:px-6 border-t border-border py-14 md:pb-20 md:pt-[4.6rem]">
        <div className="max-w-[45rem]">
          <h2 className="home-sans-heading text-4xl leading-tight md:text-5xl">Our Services</h2>
          <p className="mt-5 text-xl leading-snug text-foreground md:text-2xl">
            We work from the first product question through launch: define the work, design the
            experience, and build software that holds up in production.
          </p>
        </div>
      </section>
      <ServiceCards items={Object.values(services)} />
      <MetricStrip caseStudyId="the-information" />
      <EvidenceQuote kind="ai-strategy" />
      <SourceContactEndcap cta={defaultContactCta} />
    </>
  )
}

export const SprintZeroPage = () => {
  const service = services["sprint-zero"]

  return (
    <>
      <section className="container grid gap-10 py-16 sm:py-24 md:grid-cols-2 md:items-center lg:gap-16 lg:py-28">
        <div className="max-w-4xl">
          <h1 className="home-sans-heading text-5xl leading-[1.1] tracking-tight max-md:text-[2.5rem] md:text-[4rem]">
            <span className="home-emphasis">Sprint Zero:</span> plan it before you build it
          </h1>
          <p className="mt-5 max-w-3xl text-xl leading-snug text-foreground md:mt-6 md:text-2xl">
            A structured product design phase that aligns business goals, user needs, and technical
            considerations before development—reducing risk and creating a clear roadmap.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <ScheduleLink
              ctaLocation="sprint_zero_hero"
              className="inline-flex min-h-11 items-center justify-center rounded-full bg-primary px-6 font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Start your Sprint Zero <ArrowRight className="ml-2 size-4" aria-hidden="true" />
            </ScheduleLink>
            <a
              href="#related-work"
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-border px-6 font-semibold hover:border-primary hover:text-primary"
              onClick={() => trackCtaClick("See related work", "sprint_zero_hero", "#related-work")}
            >
              See related work
            </a>
          </div>
        </div>
        <div className="aspect-[4/3] w-full overflow-hidden rounded-3xl md:col-start-2 md:row-start-1">
          <ResponsiveImage
            assetId="hadley-feature"
            alt="Hadley product interface"
            className="size-full object-cover"
            sizes="half"
            priority
          />
        </div>
      </section>
      <BrandBand />
      <section
        className="bg-cover bg-center py-14 text-secondary-foreground md:py-16"
        style={{ backgroundImage: `url(${assetManifest["background-v2"].source})` }}
      >
        <div className="container">
          <h2 className="home-sans-heading text-4xl leading-tight md:text-5xl">Why Sprint Zero</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              [
                "Minimize Risk",
                "Identify challenges early and validate assumptions before development.",
              ],
              ["Align Teams", "Align product, business and technical teams."],
              ["Define Scope Clearly", "Establish a well-documented plan that guides development."],
              ["Accelerate Development", "Move into Agile sprints with confidence and efficiency."],
            ].map(([heading, body]) => (
              <article key={heading} className="rounded-3xl bg-secondary-foreground/15 p-6">
                <h3 className="home-sans-heading text-2xl font-bold">{heading}</h3>
                <p className="mt-3 leading-relaxed text-secondary-foreground/90">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-background py-16 text-foreground sm:py-24">
        <div className="container">
          <div className="max-w-4xl">
            <h2 className="home-sans-heading text-4xl leading-tight md:text-5xl">
              Our Structured Approach
            </h2>
            <p className="mt-6 text-xl leading-snug text-foreground md:text-[1.65rem]">
              Sprint Zero uses workshops and deliverables to define the product vision, user
              experience and technical requirements.
            </p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {service.process?.map((step) => (
              <article
                key={step.id}
                className="rounded-3xl bg-secondary p-6 text-secondary-foreground sm:p-8"
              >
                <h3 className="home-sans-heading text-2xl font-bold tracking-tight">
                  {step.heading}
                </h3>
                <p className="mt-3 leading-relaxed text-secondary-foreground/90">{step.body}</p>
                {step.items && (
                  <ul className="mt-5 space-y-2 border-t border-secondary-foreground/30 pt-5 text-sm leading-relaxed text-secondary-foreground/80">
                    {step.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>
      {service.deliverables && (
        <section className="bg-muted/40 py-16 sm:py-24">
          <div className="container">
            <div className="max-w-3xl">
              <h2 className="home-sans-heading text-4xl leading-tight md:text-5xl">
                Key deliverables from Sprint Zero
              </h2>
              <p className="mt-5 text-xl leading-snug text-muted-foreground">
                At the end of Sprint Zero, you&apos;ll have:
              </p>
            </div>
            <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {service.deliverables.map((deliverable) => (
                <article
                  key={deliverable}
                  className="rounded-3xl bg-card p-6 text-lg font-semibold leading-snug"
                >
                  {deliverable}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
      {service.proof && (
        <section
          className="marketing-surface-dark marketing-section bg-cover bg-center text-secondary-foreground"
          style={{ backgroundImage: `url(${assetManifest["background-v2"].source})` }}
        >
          <div className="container">
            <h2 className="home-sans-heading text-4xl leading-tight md:text-5xl">
              Why Tanooki Labs?
            </h2>
            <p className="mt-5 max-w-4xl text-xl leading-snug text-secondary-foreground/90">
              {service.proof.body}
            </p>
            {service.proof.items && (
              <ul className="mt-8 grid gap-5 md:grid-cols-3">
                {service.proof.items.map((item) => (
                  <li
                    key={item}
                    className="rounded-3xl bg-secondary-foreground/15 p-6 text-lg leading-relaxed text-secondary-foreground"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
      <MetricStrip caseStudyId="dapper-labs" />
      <CaseStudyFeature caseStudyIds={service.relatedCaseStudyIds ?? ["dapper-labs"]} />
      <EvidenceQuote kind="default" />
      <RelatedWork caseStudyIds={service.relatedCaseStudyIds} />
      <SourceContactEndcap
        cta={
          service.cta
            ? {
                heading: service.cta.heading,
                body: service.cta.body,
                scheduleCtaLocation: "solutions-get-started",
              }
            : defaultContactCta
        }
      />
    </>
  )
}
export const ServiceDetailView = ({
  detail,
  relatedCaseStudyIds,
}: {
  detail: ServiceDetailPage
  relatedCaseStudyIds: readonly import("~/content/cases").CaseStudyId[]
}) => {
  const heading = (accent: string | undefined, value: string) => {
    if (!accent) return value

    return (
      <>
        <span className="home-emphasis">{accent}</span> {value.slice(accent.length).trimStart()}
      </>
    )
  }

  return (
    <>
      <section className="container py-14 sm:py-20 lg:py-24">
        <div className="max-w-4xl">
          <h1 className="home-sans-heading text-5xl leading-[1.06] tracking-tight sm:text-6xl lg:text-7xl">
            {heading(detail.heroHeadingAccent, detail.heroHeading)}
          </h1>
          <p className="mt-7 max-w-3xl text-xl leading-snug sm:text-2xl">{detail.heroBody}</p>
        </div>
        <hr className="mt-12 border-foreground/35" />
      </section>
      {detail.sections.map((section, index) => (
        <section
          key={section.heading}
          className={
            index % 2 === 0
              ? "py-16 sm:py-24"
              : "bg-secondary py-16 text-secondary-foreground sm:py-24"
          }
        >
          <div className="container">
            <div className="max-w-3xl">
              <h2 className="home-sans-heading marketing-section-heading">
                {heading(section.headingAccent, section.heading)}
              </h2>
              {section.body && (
                <p className="mt-6 text-xl leading-snug sm:text-2xl">
                  {section.allSparkLink ? (
                    <>
                      {section.body.split("AllSpark")[0]}
                      <a
                        href="https://allspark.build"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-4 hover:text-primary"
                        onClick={() =>
                          trackCtaClick("AllSpark", "service_ai_enabled", "https://allspark.build")
                        }
                      >
                        AllSpark
                      </a>
                      {section.body.split("AllSpark").slice(1).join("AllSpark")}
                    </>
                  ) : (
                    section.body
                  )}
                </p>
              )}
            </div>
            {section.cards && (
              <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(18rem,.95fr)]">
                <ResponsiveImage
                  assetId={
                    caseStudies[relatedCaseStudyIds[index % relatedCaseStudyIds.length]]
                      .coverAssetId ??
                    caseStudies[relatedCaseStudyIds[index % relatedCaseStudyIds.length]]
                      .heroImageAssetId!
                  }
                  alt={`${caseStudies[relatedCaseStudyIds[index % relatedCaseStudyIds.length]].name} product interface`}
                  className="aspect-[4/3] w-full rounded-3xl object-cover"
                  sizes="half"
                  priority={index === 0}
                />
                <div className="divide-y divide-current/20">
                  {section.cards.map((card) => (
                    <article
                      key={card.heading}
                      className={
                        index % 2 === 0 ? "py-5 text-secondary-foreground" : "py-5 text-foreground"
                      }
                    >
                      {card.caseStudyId ? (
                        <Link
                          to={workDetailPath({ slug: caseStudies[card.caseStudyId].slug })}
                          className="home-sans-heading text-2xl font-normal underline underline-offset-4 hover:text-primary"
                        >
                          {card.heading}
                        </Link>
                      ) : (
                        <h3 className="home-sans-heading text-2xl font-normal">{card.heading}</h3>
                      )}
                      <p className="mt-2 text-lg leading-relaxed opacity-85">{card.body}</p>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      ))}
      {detail.allSparkProof && (
        <section
          className="marketing-surface-dark marketing-section bg-cover bg-center text-secondary-foreground"
          style={{ backgroundImage: `url(${assetManifest["background-v2"].source})` }}
        >
          <div className="container [&>*]:max-w-5xl">
            <h2 className="home-sans-heading marketing-section-heading">
              <span className="home-emphasis">{detail.allSparkProof.headingAccent}</span>{" "}
              {detail.allSparkProof.heading}
            </h2>
            <p className="mt-6 max-w-4xl text-xl leading-snug sm:text-2xl">
              {detail.allSparkProof.body}
            </p>
            <a
              href="https://allspark.build"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-primary"
              onClick={() =>
                trackCtaClick("See AllSpark", "allspark_band", "https://allspark.build")
              }
            >
              See AllSpark <ArrowRight className="size-4" aria-hidden="true" />
            </a>
          </div>
        </section>
      )}
      <MetricStrip caseStudyId={relatedCaseStudyIds[0]} />
      <CaseStudyFeature caseStudyIds={relatedCaseStudyIds} />
      {detail.featuredCaseProof && (
        <section className="bg-muted/40 py-10">
          <div className="container [&>*]:max-w-5xl">
            <Link
              to={workDetailPath({ slug: caseStudies[detail.featuredCaseProof.caseStudyId].slug })}
              className="text-xl leading-relaxed underline underline-offset-4 hover:text-primary"
            >
              {detail.featuredCaseProof.body}
            </Link>
          </div>
        </section>
      )}
      <EvidenceQuote
        kind={
          detail.title === "AI Strategy"
            ? "default"
            : detail.title === "Design"
              ? "hadley"
              : detail.title === "Product Management Services"
                ? "design"
                : "ai-strategy"
        }
      />
      <RelatedWork caseStudyIds={relatedCaseStudyIds} />
      <SourceContactEndcap
        cta={
          detail.cta
            ? {
                heading: detail.cta.heading,
                body: detail.cta.body,
                scheduleCtaLocation: "solutions-get-started",
              }
            : defaultContactCta
        }
      />
    </>
  )
}
const solutionVisuals = {
  "early-startups": "hadley-feature",
  "growth-startups": "dapper-feature-2",
  "small-medium-businesses": "t100-feature-2",
  "private-equity": "quadrant-mockup",
  "accelerators": "ss-3",
} as const

const solutionCards = Object.values(solutions).filter(
  (solution) => solution.slug !== "first-time-founders"
) as Solution[]

const splitItem = (item: string) => {
  const [title, ...body] = item.split(" — ")
  return { title, body: body.join(" — ") }
}

const SolutionCard = ({
  item,
  icon: Icon = Check,
  proofCard = false,
}: {
  item: string
  icon?: typeof Check
  proofCard?: boolean
}) => {
  const { title, body } = splitItem(item)
  return (
    <article
      className="rounded-3xl border border-border bg-card p-6 text-card-foreground sm:p-7"
      data-solution-proof-card={proofCard || undefined}
    >
      <Icon className="size-5 text-primary" aria-hidden="true" />
      <h3 className="mt-5 text-xl font-semibold">{title}</h3>
      {body && <p className="mt-3 leading-relaxed text-muted-foreground">{body}</p>}
    </article>
  )
}

const FeaturedCaseProof = ({ caseStudyId }: { caseStudyId: CaseStudyId }) => {
  const study = caseStudies[caseStudyId]
  const image = study.coverAssetId ?? study.heroImageAssetId
  if (!image) return null
  return (
    <section className="py-16 sm:py-24">
      <div className="container">
        <div className="grid overflow-hidden rounded-3xl border border-border bg-card md:grid-cols-2">
          <ResponsiveImage
            assetId={image}
            alt={`${study.name} product interface`}
            className="h-full min-h-72 w-full object-cover"
            sizes="half"
          />
          <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-14">
            <p className="marketing-eyebrow text-muted-foreground">Featured case</p>
            <h2 className="home-sans-heading marketing-section-heading mt-3">
              <span className="home-emphasis">{study.name}</span>
            </h2>
            {study.featuredStat && (
              <p className="home-sans-heading mt-5 text-3xl text-primary sm:text-4xl">
                {study.featuredStat}
              </p>
            )}
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{study.summary}</p>
            <Link
              to={workDetailPath({ slug: study.slug })}
              className="mt-7 inline-flex min-h-11 items-center gap-2 font-semibold underline underline-offset-4 hover:text-primary"
            >
              View case study <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

export const SolutionsPage = () => (
  <>
    <section className="container py-16 sm:py-24 lg:py-32">
      <div className="max-w-4xl">
        <p className="marketing-orange-text text-sm font-semibold uppercase tracking-[0.2em]">
          Solutions
        </p>
        <h1 className="home-sans-heading mt-5 text-5xl leading-[1.06] tracking-tight sm:text-6xl lg:text-7xl">
          <span className="home-emphasis">Technology solutions</span> for the work in front of you
        </h1>
        <p className="mt-6 max-w-3xl text-lg leading-relaxed sm:text-xl">
          Product and engineering support for startups, growing businesses, private equity firms and
          accelerators.
        </p>
      </div>
      <nav aria-label="Solution segments" className="mt-10 flex flex-wrap gap-3">
        {solutionCards.map((solution) => (
          <Link
            key={solution.slug}
            to={`#${solution.slug}`}
            className="inline-flex min-h-11 items-center rounded-full border border-border px-5 font-semibold hover:border-primary hover:text-primary"
          >
            {solution.name}
          </Link>
        ))}
      </nav>
    </section>
    <BrandBand />
    <section className="container py-16 sm:py-24">
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {solutionCards.map((solution) => {
          const image = solutionVisuals[solution.slug as keyof typeof solutionVisuals]
          return (
            <article
              id={solution.slug}
              key={solution.slug}
              className="overflow-hidden rounded-3xl border border-border bg-card"
            >
              <ResponsiveImage
                assetId={image}
                alt={`${solution.name} case-study product interface`}
                className="aspect-[16/9] w-full object-cover"
                sizes="third"
              />
              <div className="p-6 sm:p-7">
                <h2 className="home-sans-heading text-3xl leading-tight">{solution.name}</h2>
                <p className="mt-3 text-lg leading-relaxed text-muted-foreground">
                  {solution.description}
                </p>
                <ul className="mt-5 space-y-2 text-sm leading-relaxed">
                  {(solution.challenge?.items ?? solution.sections?.[0]?.items ?? [])
                    .slice(0, 3)
                    .map((item) => (
                      <li key={item} className="flex gap-2">
                        <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                        {splitItem(item).title}
                      </li>
                    ))}
                </ul>
                <Link
                  to={
                    {
                      "early-startups": earlyStartupsPath({}),
                      "growth-startups": growthStartupsPath({}),
                      "small-medium-businesses": smallMediumBusinessesPath({}),
                      "private-equity": privateEquityPath({}),
                      "accelerators": acceleratorsPath({}),
                    }[solution.slug] ?? workPath({})
                  }
                  className="mt-7 inline-flex min-h-11 items-center gap-2 font-semibold underline underline-offset-4 hover:text-primary"
                >
                  Learn more <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </div>
            </article>
          )
        })}
      </div>
    </section>
    <section className="bg-muted/40 py-16 sm:py-24">
      <div className="container">
        <h2 className="home-sans-heading text-4xl sm:text-5xl">Why companies choose Tanooki</h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {whyCompaniesChooseTanooki.map((point, index) => {
            const Icon = [Rocket, Users, Sparkles, Compass][index]
            return (
              <article key={point.title} className="rounded-3xl border border-border bg-card p-6">
                <Icon className="size-5 text-primary" aria-hidden="true" />
                <h3 className="mt-5 text-xl font-semibold">{point.title}</h3>
                <p className="mt-3 leading-relaxed text-muted-foreground">{point.body}</p>
              </article>
            )
          })}
        </div>
      </div>
    </section>
    <EvidenceQuote kind="design" />
    <SourceContactEndcap
      cta={{
        heading: "Get Started Today",
        body: "Schedule a free 30-minute consultation to discuss your project and explore the best approach for your business.",
        scheduleCtaLocation: "solutions-get-started",
      }}
    />
  </>
)

export const SolutionPage = ({ solution }: { solution: Solution }) => {
  const heroImage = solutionVisuals[solution.slug as keyof typeof solutionVisuals]
  const caseStudyId = solution.relatedCaseStudyIds?.[0] ?? "hadley"
  const challengeItems =
    solution.challenge?.items ?? solution.sections?.[0]?.items?.slice(0, 4) ?? []
  const isPrivateEquity = solution.slug === "private-equity"
  const isAccelerators = solution.slug === "accelerators"
  return (
    <>
      <section className="container grid gap-10 py-16 sm:py-24 md:grid-cols-2 md:items-center lg:gap-16 lg:py-28">
        <div className="max-w-4xl" data-solution-hero-copy>
          <p className="marketing-orange-text text-sm font-semibold uppercase tracking-[0.2em]">
            Solutions
          </p>
          <h1 className="home-sans-heading mt-5 text-4xl leading-[1.04] tracking-tight sm:text-5xl md:text-5xl lg:text-6xl xl:text-7xl">
            <span className="home-emphasis">
              {solution.heading?.split(":")[0] ?? solution.name}
            </span>
            {solution.heading?.includes(":")
              ? `:${solution.heading.split(":").slice(1).join(":")}`
              : ""}
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed sm:text-xl">{solution.intro?.[0]}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <ScheduleLink
              ctaLocation={
                solutionScheduleCtaLocations[
                  solution.slug as keyof typeof solutionScheduleCtaLocations
                ]
              }
              className="inline-flex min-h-11 items-center justify-center rounded-full bg-primary px-6 font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Schedule a call <ArrowRight className="ml-2 size-4" aria-hidden="true" />
            </ScheduleLink>
            <a
              href="#related-work"
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-border px-6 font-semibold hover:border-primary hover:text-primary"
            >
              See related work
            </a>
          </div>
        </div>
        <div
          className={cn(
            "aspect-[4/3] w-full overflow-hidden rounded-3xl md:col-start-2 md:row-start-1",
            !isAccelerators && "md:max-h-[22rem]"
          )}
        >
          <ResponsiveImage
            assetId={heroImage}
            alt={`${caseStudies[caseStudyId].name} product interface`}
            className={`size-full object-cover ${isAccelerators ? "object-center" : ""}`}
            dataSolutionHeroImage
            sizes="half"
            priority
          />
        </div>
      </section>
      <section className="border-y border-border bg-muted/30 py-8 sm:py-10">
        <div className="container grid gap-6 sm:grid-cols-3">
          {[
            ["200+", "product launches"],
            ["91%", "of clients still operating or acquired"],
            ["US & Canada", "senior team"],
          ].map(([value, label]) => (
            <div key={value}>
              <p className="home-sans-heading text-4xl font-bold leading-none tracking-tight sm:text-5xl">
                {value}
              </p>
              <p className="mt-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
              </p>
            </div>
          ))}
        </div>
      </section>
      <BrandBand />
      {isAccelerators && (
        <section className="bg-muted/40 py-16 sm:py-24">
          <div className="container grid gap-5 lg:grid-cols-[1fr_2fr] lg:items-start">
            <p className="home-sans-heading text-3xl">Born inside an incubator</p>
            <div className="max-w-2xl space-y-4 leading-relaxed text-muted-foreground">
              <p>
                Tanooki Labs grew from helping set up and operate an incubator inside AOL Ventures.
              </p>
              <p>
                That experience showed us the challenge of giving consistent technology and product
                support across a portfolio of companies.
              </p>
            </div>
          </div>
        </section>
      )}
      <section className="py-16 sm:py-24">
        <div className="container">
          <div className="max-w-3xl">
            <h2 className="home-sans-heading text-4xl sm:text-5xl">The challenge</h2>
            {solution.challenge?.body && (
              <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
                {solution.challenge.body}
              </p>
            )}
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {challengeItems.map((item, index) => (
              <SolutionCard
                key={item}
                item={item}
                icon={[Compass, Users, Lightbulb, CircleDot][index] ?? Check}
              />
            ))}
          </div>
        </div>
      </section>
      <section className="bg-muted/40 py-16 sm:py-24">
        <div className="container">
          <h2 className="home-sans-heading text-4xl sm:text-5xl">How we help</h2>
          {isPrivateEquity ? (
            <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_auto_1fr] lg:items-stretch">
              {solution.sections?.map((section, index) => (
                <div key={section.id} className="contents">
                  <article className="rounded-3xl border border-border bg-card p-7">
                    <p className="marketing-eyebrow text-primary">
                      {index === 0 ? "Pre-acquisition" : "Post-acquisition"}
                    </p>
                    <h3 className="mt-4 text-2xl font-semibold">
                      {section.heading.replace(/^[^:]+:\s*/, "")}
                    </h3>
                    <p className="mt-3 leading-relaxed text-muted-foreground">{section.body}</p>
                    <ul className="mt-6 space-y-3">
                      {section.items?.map((item) => (
                        <li key={item} className="flex gap-3">
                          <Check className="mt-1 size-4 shrink-0 text-primary" aria-hidden="true" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                  {index === 0 && (
                    <ArrowRight
                      className="hidden size-8 text-primary lg:block"
                      aria-hidden="true"
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {(solution.steps ?? solution.sections ?? []).map((step, index) => {
                return (
                  <article
                    key={step.id}
                    className="relative rounded-3xl border border-border bg-card p-6 sm:p-7"
                  >
                    <p className="home-sans-heading text-4xl font-bold text-primary">
                      0{index + 1}
                    </p>
                    <h3 className="mt-4 text-2xl font-semibold">{step.heading}</h3>
                    <ul className="mt-5 space-y-3 text-sm leading-relaxed">
                      {step.items?.map((item) => (
                        <li key={item} className="flex gap-2">
                          <Check
                            className="mt-0.5 size-4 shrink-0 text-primary"
                            aria-hidden="true"
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </article>
                )
              })}
            </div>
          )}
        </div>
      </section>
      {isAccelerators && (
        <section className="border-y border-border py-6">
          <div className="container text-center text-sm font-semibold leading-relaxed">
            Founders we&apos;ve worked with came through Y Combinator · Techstars · Interplay
            Ventures
          </div>
        </section>
      )}
      <FeaturedCaseProof caseStudyId={caseStudyId} />
      {solution.proof && (
        <section className="bg-secondary py-16 text-secondary-foreground sm:py-24">
          <div className="container">
            <div className="max-w-3xl">
              <h2 className="home-sans-heading text-4xl sm:text-5xl">Why choose Tanooki</h2>
              {solution.proof.body && (
                <p className="mt-5 text-lg leading-relaxed text-secondary-foreground/80">
                  {solution.proof.body}
                </p>
              )}
            </div>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 xl:grid-cols-5">
              {solution.proof.items?.map((item, index) => (
                <SolutionCard
                  key={item}
                  item={item}
                  icon={[Rocket, Users, Sparkles, Lightbulb, Compass][index] ?? Check}
                  proofCard
                />
              ))}
            </div>
          </div>
        </section>
      )}
      <EvidenceQuote
        kind={
          solution.slug === "early-startups" || solution.slug === "accelerators"
            ? "hadley"
            : solution.slug === "growth-startups"
              ? "default"
              : solution.slug === "small-medium-businesses"
                ? "ai-strategy"
                : solution.slug === "private-equity"
                  ? "design"
                  : "default"
        }
      />
      <RelatedWork caseStudyIds={solution.relatedCaseStudyIds} />
      <SourceContactEndcap
        cta={
          solution.cta
            ? {
                heading: solution.cta.heading,
                body: solution.cta.body,
                scheduleCtaLocation:
                  solutionScheduleCtaLocations[
                    solution.slug as keyof typeof solutionScheduleCtaLocations
                  ],
              }
            : undefined
        }
      />
    </>
  )
}
const AboutPage = () => (
  <>
    <PageHeader
      eyebrow="About Tanooki"
      heading="Fast growing, just like our clients."
      accent="our clients"
      breakAfter="Fast growing,"
      body="We are a senior, multidisciplinary team that brings clarity to complicated product work and follows through on the details."
    />
    <CallToAction />
  </>
)

const ApproachPage = () => {
  const content = staticPages["our-approach"]
  const steps = [
    "Get aligned on the opportunity, constraints, and the evidence that matters.",
    "Create a practical plan, prototype the important decisions, and build shared confidence.",
    "Ship in focused increments with the quality and care a lasting product deserves.",
  ]
  return (
    <>
      <PageHeader {...content} />
      <section className="container grid gap-6 pb-12 md:grid-cols-3 lg:pb-[4.5rem]">
        {["Understand", "Shape", "Deliver"].map((step, index) => (
          <Card key={step}>
            <CardHeader>
              <CardTitle>{`${index + 1}. ${step}`}</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">{steps[index]}</CardContent>
          </Card>
        ))}
      </section>
      <CallToAction />
    </>
  )
}

const TeamPage = () => {
  return (
    <>
      <section className="container marketing-hero">
        <h1 className="home-sans-heading marketing-page-title">
          Meet the <span className="home-emphasis home-highlight">Tanooki Team</span>
        </h1>
        <hr className="mt-10 border-foreground/35 lg:mt-14" />
        <div className="grid grid-cols-2 gap-x-4 pt-8 lg:grid-cols-4 lg:gap-x-10">
          {teamMemberIds.map((id) => {
            const member = teamMembers[id]
            return (
              <article key={member.slug} className="pb-6 lg:pb-[3.75rem]">
                <ResponsiveImage
                  assetId={member.imageAssetId}
                  alt={member.name}
                  className="aspect-square w-full object-cover"
                  sizes="grid"
                />
                <h2 className="home-sans-heading mt-3 text-lg leading-snug lg:mt-5 lg:text-xl">
                  {member.name}
                </h2>
                <p className="mt-1 text-base leading-snug text-muted-foreground">{member.title}</p>
              </article>
            )
          })}
        </div>
      </section>
      <CallToAction />
    </>
  )
}
const ContactPage = () => {
  return (
    <>
      <section className="container marketing-hero grid gap-12 lg:grid-cols-2 lg:gap-20">
        <div className="max-w-[34rem] lg:pt-6">
          <h1 className="home-sans-heading marketing-page-title marketing-split-title">
            Excited to <span className="home-emphasis home-highlight">hear from you!</span>
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">
            Interested in working with us? Ready to start a project? If you&apos;re excited,
            we&apos;re excited. Drop us a line to start a conversation.
          </p>
        </div>
        <div className="lg:pt-2 [&_form]:max-w-none [&_button]:rounded-full [&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary/90">
          <ContactForm sourceLayout />
        </div>
      </section>
      <BrandBand />
      <section
        className="marketing-surface-dark marketing-section bg-cover bg-center text-secondary-foreground"
        style={{ backgroundImage: `url(${assetManifest["footer-background-hr"].source})` }}
      >
        <div className="container grid gap-12 lg:grid-cols-2 lg:items-start lg:gap-20">
          <div className="max-w-xl lg:order-2">
            <h2 className="home-sans-heading marketing-section-heading">
              Schedule time with one of the Tanooki Co-Founders
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-secondary-foreground/80 sm:text-xl">
              If you have ideas for your brand, your business, or the world at large, we&apos;re
              here to listen and collaborate.
            </p>
          </div>
          <div className="grid gap-5 lg:order-1 lg:max-w-[586px] lg:grid-cols-2 lg:gap-8">
            {(["david-renz", "eric-skiff"] as const).map((id) => {
              const founder = teamMembers[id]
              return (
                <article
                  key={founder.slug}
                  className="overflow-hidden rounded-3xl bg-secondary-foreground/15 lg:overflow-visible lg:rounded-none lg:bg-transparent"
                >
                  <ResponsiveImage
                    assetId={founder.imageAssetId}
                    alt={founder.name}
                    className="aspect-square w-full object-cover lg:aspect-[277/350]"
                    sizes="grid"
                  />
                  <div className="p-5 lg:mt-4 lg:flex lg:items-center lg:justify-between lg:px-0 lg:py-px">
                    <div>
                      <h3 className="home-sans-heading text-2xl font-semibold lg:text-xl lg:font-normal lg:leading-6">
                        {founder.name}
                      </h3>
                      <p className="mt-1 text-sm text-secondary-foreground/80 lg:leading-5">
                        Co-Founder
                      </p>
                    </div>
                    <ScheduleLink
                      ctaLocation={founderScheduleCtaLocations[id]}
                      ariaLabel={`Schedule a 30-minute call with ${calendlyEventOwnerName}`}
                      className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 lg:mt-0"
                    >
                      Calendly <ArrowRight className="size-4" aria-hidden="true" />
                    </ScheduleLink>
                  </div>
                </article>
              )
            })}
          </div>
        </div>
      </section>
      <section className="marketing-surface-primary marketing-section bg-primary text-primary-foreground">
        <div className="container text-center">
          <h2 className="home-sans-heading marketing-section-heading">
            Subscribe to our newsletter
          </h2>
          <div className="mx-auto mt-7 max-w-md text-left [&_button]:w-full [&_button]:rounded-full [&_button]:bg-secondary [&_button]:text-secondary-foreground [&_button]:hover:bg-secondary/90 [&_input]:border-0 [&_input]:bg-background [&_label]:sr-only">
            <NewsletterForm placement="contact-page" />
          </div>
        </div>
      </section>
    </>
  )
}
const StaticPage = ({ page }: { page: StaticPageId }) => <PageHeader {...staticPages[page]} />

export const PublicPage = ({ page }: { page: PublicPageId }) => {
  switch (page) {
    case "home":
      return <HomePage />
    case "services":
      return <DudaCopyPage page="services" />
    case "ai-strategy":
      return <ServicePage page="ai-strategy" />
    case "web-development":
      return <ServicePage page="web-development" />
    case "design":
      return <ServicePage page="design" />
    case "product":
      return <ServicePage page="product" />
    case "sprint-zero":
      return <ServicePage page="sprint-zero" />
    case "solutions":
      return <SolutionsOverviewPage />
    case "early-startups":
      return <SolutionDetailPage page="early-startups" />
    case "growth-startups":
      return <SolutionDetailPage page="growth-startups" />
    case "small-medium-businesses":
      return <SolutionDetailPage page="small-medium-businesses" />
    case "private-equity":
      return <SolutionDetailPage page="private-equity" />
    case "accelerators":
      return <SolutionDetailPage page="accelerators" />
    case "about":
      return <AboutPage />
    case "our-approach":
      return <ApproachPage />
    case "our-team":
      return <TeamPage />
    case "contact":
      return <ContactPage />
    case "first-time-founders":
      return <FirstTimeFoundersPage />
    case "privacy":
      return <StaticPage page="privacy" />
    case "terms":
      return <StaticPage page="terms" />
    default:
      throw page satisfies never
  }
}
